import { chatHistorySampleData } from '../constants/chatHistory'

import { AuthUser, ChatMessage, Conversation, ConversationRequest, CosmosDBHealth, CosmosDBStatus, CVUploadTask, LoginResponse, UserInfo } from './models'

// Authentication API functions

export type AgencyOption = {
  agencyid: number
  agency_name: string
  role?: string
}

export type TwoFAResponse = {
  success: boolean
  requires_2fa?: boolean
  requires_agency_selection?: boolean
  pending_token?: string
  select_token?: string
  agencies?: AgencyOption[]
  masked_email?: string
  message?: string
  error?: string
  user?: AuthUser
}

export async function login(email: string, password: string): Promise<TwoFAResponse> {
  try {
    const response = await fetch('/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include'
    })

    const data = await response.json()

    if (response.ok && data.success) {
      return data as TwoFAResponse
    } else {
      return {
        success: false,
        error: data.error || 'Login failed'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: 'Network error. Please try again.'
    }
  }
}

export async function verify2FA(pendingToken: string, code: string): Promise<TwoFAResponse> {
  try {
    const response = await fetch('/auth/verify-2fa', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ pending_token: pendingToken, code }),
      credentials: 'include'
    })

    const data = await response.json()

    if (response.ok && data.success) {
      return data as TwoFAResponse
    } else {
      return {
        success: false,
        error: data.error || 'Verification failed'
      }
    }
  } catch (error) {
    return {
      success: false,
      error: 'Network error. Please try again.'
    }
  }
}

// Step 3 (only when the account belongs to >1 agency): exchange the verified
// select_token + chosen agency for a session.
export async function selectAgency(selectToken: string, agencyid: number): Promise<LoginResponse> {
  try {
    const response = await fetch('/auth/select-agency', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ select_token: selectToken, agencyid }),
      credentials: 'include'
    })

    const data = await response.json()

    if (response.ok && data.success) {
      return { success: true, user: data.user as AuthUser }
    }
    return { success: false, error: data.error || 'Agency selection failed' }
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' }
  }
}

// Switch the active agency of the current session (profile-menu switcher).
export async function switchAgency(agencyid: number): Promise<LoginResponse> {
  try {
    const response = await fetch('/auth/switch-agency', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ agencyid }),
      credentials: 'include'
    })

    const data = await response.json()

    if (response.ok && data.success) {
      return { success: true, user: data.user as AuthUser }
    }
    return { success: false, error: data.error || 'Failed to switch agency' }
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' }
  }
}

export async function logout(): Promise<boolean> {
  try {
    const response = await fetch('/auth/logout', {
      method: 'POST',
      credentials: 'include'
    })
    return response.ok
  } catch (error) {
    return false
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await fetch('/auth/me', {
      method: 'GET',
      credentials: 'include'
    })

    if (response.ok) {
      const data = await response.json()
      return data.user as AuthUser
    }
    return null
  } catch (error) {
    return null
  }
}

export async function conversationApi(options: ConversationRequest, abortSignal: AbortSignal): Promise<Response> {
  const response = await fetch('/conversation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages: options.messages
    }),
    signal: abortSignal
  })

  return response
}

export async function getUserInfo(): Promise<UserInfo[]> {
  const response = await fetch('/.auth/me')
  if (!response.ok) {
    console.log('No identity provider found. Access to chat will be blocked.')
    return []
  }

  const payload = await response.json()
  return payload
}

// export const fetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
export const fetchChatHistoryInit = (): Conversation[] | null => {
  // Make initial API call here

  return chatHistorySampleData
}

export const historyList = async (offset = 0): Promise<Conversation[] | null> => {
  // Only fetch the conversation list metadata. Messages are loaded
  // lazily when the user clicks a conversation (see the click handler
  // in ChatHistoryListItem.tsx). Previously this function pre-loaded
  // the full message history for every returned conversation in
  // parallel, which cost one DB round-trip per conversation on every
  // login and made the chat history panel take 5-10 seconds to appear.
  const response = await fetch(`/history/list?offset=${offset}`, {
    method: 'GET',
    credentials: 'include'
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const conversations: Conversation[] = payload.map((conv: any) => ({
        id: conv.id,
        title: conv.title,
        date: conv.createdAt,
        messages: []
      }))
      return conversations
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  const response = await fetch('/history/read', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(async res => {
      if (!res) {
        return []
      }
      const payload = await res.json()
      const messages: ChatMessage[] = []
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            date: msg.createdAt,
            content: msg.content,
            feedback: msg.feedback ?? undefined
          }
          messages.push(message)
        })
      }
      return messages
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return []
    })
  return response
}

export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,
  convId?: string
): Promise<Response> => {
  let body
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      messages: options.messages
    })
  } else {
    body = JSON.stringify({
      messages: options.messages
    })
  }
  const response = await fetch('/history/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: body,
    signal: abortSignal,
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return new Response()
    })
  return response
}

export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response> => {
  const response = await fetch('/history/update', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDelete = async (convId: string): Promise<Response> => {
  const response = await fetch('/history/delete', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (): Promise<Response> => {
  const response = await fetch('/history/delete_all', {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyClear = async (convId: string): Promise<Response> => {
  const response = await fetch('/history/clear', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyRename = async (convId: string, title: string): Promise<Response> => {
  const response = await fetch('/history/rename', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const response = await fetch('/history/ensure', {
    method: 'GET',
    credentials: 'include'
  })
    .then(async res => {
      const respJson = await res.json()
      let formattedResponse
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        return {
          cosmosDB: false,
          status: formattedResponse
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.')
      return {
        cosmosDB: false,
        status: err
      }
    })
  return response
}

// Email History API functions
export interface EmailDaySummary {
  date: string
  candidate_count: number
  client_count: number
}

export interface EmailDetail {
  emaillogid: number
  type: 'candidate' | 'client'
  entity_id: number
  sender_email: string
  recipient_email: string
  subject: string
  body_html: string | null
  body_plain: string | null
  status: string
  error_message: string | null
  retry_count: number
  createdate: string
}

export const getEmailsByDay = async (): Promise<EmailDaySummary[]> => {
  try {
    const response = await fetch('/emails/by-day', {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching emails by day')
      return []
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching emails by day:', error)
    return []
  }
}

export const getEmailsForDate = async (date: string): Promise<EmailDetail[]> => {
  try {
    const response = await fetch(`/emails/by-day/${date}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching emails for date')
      return []
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching emails for date:', error)
    return []
  }
}

export const requeueEmail = async (emaillogid: number, type: 'candidate' | 'client'): Promise<boolean> => {
  try {
    const response = await fetch('/emails/requeue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ emaillogid, type })
    })
    return response.ok
  } catch (error) {
    console.error('Error requeuing email:', error)
    return false
  }
}

// Email Queue Processing
export interface ProcessQueueResponse {
  message: string
  queued_count: number
  candidate_queued: number
  client_queued: number
}

export interface LogEntry {
  time: string
  message: string
  level: 'info' | 'success' | 'error' | 'warning'
}

export interface QueueStatusResponse {
  progress: {
    total: number
    sent: number
    failed: number
    status: string
    current: number
  }
  candidate_stats: { queued: number; sending: number; sent: number; failed: number }
  client_stats: { queued: number; sending: number; sent: number; failed: number }
  total_queued: number
  total_sending: number
  log: LogEntry[]
}

export const processEmailQueue = async (): Promise<ProcessQueueResponse | null> => {
  try {
    const response = await fetch('/emails/process-queue', {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json()
      console.error('Error processing email queue:', data.error)
      return null
    }
    return await response.json()
  } catch (error) {
    console.error('Error processing email queue:', error)
    return null
  }
}

export const getEmailQueueStatus = async (): Promise<QueueStatusResponse | null> => {
  try {
    const response = await fetch('/emails/process-queue/status', {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching email queue status')
      return null
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching email queue status:', error)
    return null
  }
}

// Candidates List
export interface CandidateSummary {
  id: number
  name: string
  job_title: string
  location: string
  availability: string
  last_modified: string
  status: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
}

export const getCandidateList = async (limit = 200, offset = 0): Promise<PaginatedResponse<CandidateSummary>> => {
  try {
    const response = await fetch(`/candidates/list?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching candidates list')
      return { items: [], total: 0 }
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching candidates list:', error)
    return { items: [], total: 0 }
  }
}

// Candidate Detail
export interface CandidateDetail {
  id: number
  first_name: string
  last_name: string
  email: string
  job_title: string
  created: string
  modified: string
  status: string
  disabled_reason: string
  disabled_date: string
  information: Record<string, any>
}

export const getCandidateDetail = async (id: number): Promise<CandidateDetail | null> => {
  try {
    const response = await fetch(`/candidates/${id}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error('Error fetching candidate detail:', error)
    return null
  }
}

export const disableCandidate = async (id: number, reason: string): Promise<boolean> => {
  try {
    const response = await fetch(`/candidates/${id}/disable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason })
    })
    return response.ok
  } catch (error) {
    console.error('Error disabling candidate:', error)
    return false
  }
}

export const enableCandidate = async (id: number): Promise<boolean> => {
  try {
    const response = await fetch(`/candidates/${id}/enable`, {
      method: 'POST',
      credentials: 'include'
    })
    return response.ok
  } catch (error) {
    console.error('Error enabling candidate:', error)
    return false
  }
}

// Clients List
export interface ClientSummary {
  id: number
  company_name: string
  industry: string
  contact_person: string
  email: string
  phone: string
  send_communication: boolean
  last_modified: string
}

export const getClientList = async (limit = 200, offset = 0): Promise<PaginatedResponse<ClientSummary>> => {
  try {
    const response = await fetch(`/clients/list?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching clients list')
      return { items: [], total: 0 }
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching clients list:', error)
    return { items: [], total: 0 }
  }
}

// Client Detail
export interface ClientDetail {
  id: number
  company_name: string
  industry: string
  contact_person: string
  email: string
  phone: string
  address: string
  website: string
  notes: string
  send_communication: boolean
  send_communication_reason: string
  created: string
  modified: string
}

export const getClientDetail = async (id: number): Promise<ClientDetail | null> => {
  try {
    const response = await fetch(`/clients/${id}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error('Error fetching client detail:', error)
    return null
  }
}

export const toggleClientCommunication = async (id: number, reason?: string): Promise<{ send_communication: boolean; send_communication_reason: string } | null> => {
  try {
    const response = await fetch(`/clients/${id}/toggle-communication`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason: reason || '' })
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error('Error toggling client communication:', error)
    return null
  }
}

// Active Jobs & Candidate Matching
export interface ActiveJob {
  jobid: number
  clientid: number | null
  client_name: string
  title: string
  location: string
  status: string
  skills: string
  experience_level: string
  salary_range: string
}

export interface CandidateMatch {
  candidateid: number
  name: string
  match_score: number
  reasons: string[]
  skills: string
  location: string
  distance_warning: string | null
}

export interface NearbyCandidate {
  candidateid: number
  name: string
  location: string
  distance_note: string
}

export interface MatchResult {
  top_candidates: CandidateMatch[]
  nearby_candidates: NearbyCandidate[]
}

export const getActiveJobs = async (): Promise<ActiveJob[]> => {
  try {
    const response = await fetch('/jobs/active', {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching active jobs')
      return []
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching active jobs:', error)
    return []
  }
}

export interface ScheduledInterview {
  interviewid: number
  candidate_name: string
  interview_date: string
  interview_time: string
  interview_type: string
  location: string
  outcome: string
  notes: string
}

export interface JobApplication {
  applicationid: number
  candidateid: number
  candidate_name: string
  status: string
  applied_date: string
  stage: string
  notes: string
  has_interviews: boolean
}

export const getJobApplications = async (jobid: number): Promise<JobApplication[]> => {
  try {
    const response = await fetch(`/jobs/${jobid}/applications`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to fetch applications')
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching job applications:', error)
    throw error
  }
}

export const getApplicationInterviews = async (applicationid: number): Promise<ScheduledInterview[]> => {
  try {
    const response = await fetch(`/applications/${applicationid}/interviews`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to fetch interviews')
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching application interviews:', error)
    throw error
  }
}

export const getJobInterviews = async (jobid: number): Promise<ScheduledInterview[]> => {
  try {
    const response = await fetch(`/jobs/${jobid}/scheduled-interviews`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to fetch interviews')
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching job interviews:', error)
    throw error
  }
}

export const updateInterview = async (interviewid: number, data: { outcome?: string; notes?: string }): Promise<boolean> => {
  try {
    const response = await fetch(`/interviews/${interviewid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    })
    return response.ok
  } catch (error) {
    console.error('Error updating interview:', error)
    return false
  }
}

export const getJobCandidateMatches = async (jobid: number): Promise<MatchResult> => {
  try {
    const response = await fetch(`/jobs/${jobid}/match-candidates`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to match candidates')
    }
    return await response.json()
  } catch (error) {
    console.error('Error matching candidates:', error)
    throw error
  }
}

// Market Intelligence API functions
export interface MarketJobSummary {
  marketjobid: number
  source: string
  source_url: string | null
  company_name: string
  job_title: string
  location: string
  salary_range: string
  contact_email: string | null
  contact_phone: string | null
  status: string
  createdate: string | null
}

export interface MarketJobDetail extends MarketJobSummary {
  source_job_id: string | null
  description: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  information: string | null
  clientid: number | null
  assessed_by: number | null
  assessed_date: string | null
  modifydate: string | null
}

export const getMarketJobs = async (status?: string, search?: string): Promise<MarketJobSummary[]> => {
  try {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (search) params.set('search', search)
    const qs = params.toString()
    const response = await fetch(`/market-jobs${qs ? '?' + qs : ''}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      console.error('Error fetching market jobs')
      return []
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching market jobs:', error)
    return []
  }
}

export const getMarketJobDetail = async (marketjobid: number): Promise<MarketJobDetail | null> => {
  try {
    const response = await fetch(`/market-jobs/${marketjobid}`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error('Error fetching market job detail:', error)
    return null
  }
}

export const assessMarketJob = async (marketjobid: number): Promise<boolean> => {
  try {
    const response = await fetch(`/market-jobs/${marketjobid}/assess`, {
      method: 'POST',
      credentials: 'include'
    })
    return response.ok
  } catch (error) {
    console.error('Error assessing market job:', error)
    return false
  }
}

export const makeClientFromMarketJob = async (
  marketjobid: number,
  clientData: Record<string, string>
): Promise<{ success: boolean; clientid?: number; error?: string }> => {
  try {
    const response = await fetch(`/market-jobs/${marketjobid}/make-client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(clientData)
    })
    const data = await response.json()
    if (response.ok && data.success) {
      return { success: true, clientid: data.clientid }
    }
    return { success: false, error: data.error || 'Failed to create client' }
  } catch (error) {
    console.error('Error making client from market job:', error)
    return { success: false, error: 'Network error' }
  }
}

export const getMarketJobCandidateMatches = async (marketjobid: number): Promise<MatchResult> => {
  try {
    const response = await fetch(`/market-jobs/${marketjobid}/match-candidates`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to match candidates')
    }
    return await response.json()
  } catch (error) {
    console.error('Error matching candidates for market job:', error)
    throw error
  }
}

export const frontendSettings = async (): Promise<Response | null> => {
  const response = await fetch('/frontend_settings', {
    method: 'GET'
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}
export const historyMessageFeedback = async (messageId: string, feedback: string): Promise<Response> => {
  const response = await fetch('/history/message_feedback', {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback
    }),
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

// Background CV Upload API functions

export async function uploadCVBackground(file: File, updateExisting: boolean = false): Promise<{ task_id: string; message: string } | null> {
  try {
    const formData = new FormData()
    formData.append('file', file)
    if (updateExisting) {
      formData.append('update_existing', 'true')
    }
    const response = await fetch('/upload/cv/background', {
      method: 'POST',
      body: formData,
      credentials: 'include'
    })
    if (response.ok) {
      return await response.json()
    }
    const error = await response.json()
    throw new Error(error.error || 'Upload failed')
  } catch (error) {
    console.error('Error uploading CV:', error)
    throw error
  }
}

export async function getCVUploadStatus(): Promise<CVUploadTask[]> {
  try {
    const response = await fetch('/upload/cv/background/status', { credentials: 'include' })
    if (response.ok) {
      return await response.json()
    }
    return []
  } catch {
    return []
  }
}

export async function resolveCVDuplicate(taskId: string, action: 'update_existing' | 'force_create', existingCandidateId?: number): Promise<boolean> {
  try {
    const response = await fetch(`/upload/cv/background/${taskId}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, existing_candidate_id: existingCandidateId }),
      credentials: 'include'
    })
    return response.ok
  } catch {
    return false
  }
}

export async function submitCVMissingFields(taskId: string, fieldValues: Record<string, string>): Promise<boolean> {
  try {
    const response = await fetch(`/upload/cv/background/${taskId}/missing-fields`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ field_values: fieldValues }),
      credentials: 'include'
    })
    return response.ok
  } catch {
    return false
  }
}

export async function dismissCVTask(taskId: string): Promise<boolean> {
  try {
    const response = await fetch(`/upload/cv/background/${taskId}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    return response.ok
  } catch {
    return false
  }
}

// =========================================================================
// Candidate messages inbox (agency side)
// =========================================================================

export interface CandidateMessageThreadSummary {
  candidateid: number
  candidate_name: string
  candidate_email?: string
  last_message_at: string
  last_message_preview: string
  last_message_sender: 'candidate' | 'agency' | 'bot'
  unread_count: number
  message_count: number
}

export interface CandidateMessageItem {
  messageid: number
  sender_type: 'candidate' | 'agency' | 'bot'
  sender_name: string
  body: string
  created_at: string
  read_by_candidate_at: string | null
  read_by_agency_at: string | null
}

export interface CandidateMessageThread {
  candidateid: number
  candidate_name: string
  candidate_email?: string
  messages: CandidateMessageItem[]
}

export async function agencyListCandidateThreads(): Promise<
  | { threads: CandidateMessageThreadSummary[]; total_unread: number }
  | { error: string }
> {
  try {
    const res = await fetch('/api/candidate-messages/threads', { credentials: 'include' })
    const data = await res.json()
    if (!res.ok) return { error: data.error || `HTTP ${res.status}` }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' }
  }
}

export async function agencyGetCandidateThread(
  candidateid: number,
): Promise<CandidateMessageThread | { error: string }> {
  try {
    const res = await fetch(`/api/candidate-messages/threads/${candidateid}`, {
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || `HTTP ${res.status}` }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' }
  }
}

export async function agencyReplyCandidateThread(
  candidateid: number,
  body: string,
): Promise<{ success: boolean; messages: CandidateMessageItem[] } | { error: string }> {
  try {
    const res = await fetch(`/api/candidate-messages/threads/${candidateid}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || `HTTP ${res.status}` }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' }
  }
}

export async function agencyCandidateMessagesUnreadCount(): Promise<number> {
  try {
    const res = await fetch('/api/candidate-messages/unread-count', { credentials: 'include' })
    if (!res.ok) return 0
    const data = await res.json()
    return data.unread_count ?? 0
  } catch {
    return 0
  }
}

// ============================================================
// Activity logs (agency admin portal)
// ============================================================

export interface ActivityLogEvent {
  logid: number
  timestamp: string
  action: string
  userid: number | null
  username: string | null
  full_name: string | null
  entity_type: string | null
  entity_id: number | null
  candidateid: number | null
  detail_email: string | null
  ip_address: string | null
  details: Record<string, any> | null
}

export interface ActivityLogStats {
  window_days: number
  total_events: number
  active_users: number
  active_candidates: number
  by_action: Record<string, number>
  daily: { day: string; count: number }[]
  top_users: { username: string | null; full_name: string | null; count: number }[]
}

export async function getActivityLogStats(days = 7): Promise<ActivityLogStats | null> {
  try {
    const res = await fetch(`/activity-logs/stats?days=${days}`, { credentials: 'include' })
    if (!res.ok) return null
    return await res.json()
  } catch (e) {
    console.error('Error fetching activity log stats:', e)
    return null
  }
}

export interface ActivityLogQuery {
  days?: number
  action?: string
  q?: string
  limit?: number
  offset?: number
}

export async function getActivityLogs(query: ActivityLogQuery = {}): Promise<ActivityLogEvent[]> {
  try {
    const params = new URLSearchParams()
    if (query.days != null) params.set('days', String(query.days))
    if (query.action) params.set('action', query.action)
    if (query.q) params.set('q', query.q)
    if (query.limit != null) params.set('limit', String(query.limit))
    if (query.offset != null) params.set('offset', String(query.offset))
    const res = await fetch(`/activity-logs?${params.toString()}`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json()
    return data.events || []
  } catch (e) {
    console.error('Error fetching activity logs:', e)
    return []
  }
}

export function activityLogReportUrl(query: ActivityLogQuery = {}): string {
  const params = new URLSearchParams()
  if (query.days != null) params.set('days', String(query.days))
  if (query.action) params.set('action', query.action)
  if (query.q) params.set('q', query.q)
  return `/activity-logs/report?${params.toString()}`
}
